import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { of, tap } from 'rxjs';

import { AuthUserModel } from '~/api/auth/auth-user.model';
import { TokenStore } from '~/api/auth/token-store';
import { decodeAccessToken, isAccessTokenExpired } from '~/api/auth/decode-access-token';

import { environment } from '../../../environments/environment';

const RETURN_URL_KEY = 'gp-return-url';
const OAUTH_LOGIN_ERROR_STORAGE_KEY = 'oauth-login-error';

@Injectable({ providedIn: 'root' })
export class AuthService {
  readonly currentUser = computed<AuthUserModel>(() => this.user() ?? { sub: '', name: 'N/A' });

  readonly userRoles = computed(() => {
    const { role } = this.currentUser();

    if (!role) {
      return [];
    }

    return Array.isArray(role) ? role : [role];
  });

  name = computed(() => this.currentUser().name ?? 'N/A');

  private http = inject(HttpClient);
  private router = inject(Router);
  private tokenStore = inject(TokenStore);

  private readonly user = signal<AuthUserModel | null>(this.readStoredUser());

  isAuthenticated(): boolean {
    const token = this.tokenStore.get();
    return !!token && !isAccessTokenExpired(token);
  }

  checkAuth() {
    this.handleOauthCallback();
    this.restoreSession();
    return of(this.isAuthenticated());
  }

  login(username: string, password: string) {
    return this.http
      .post<{ accessToken: string }>(`${environment.api}/auth/login`, { username, password })
      .pipe(tap(({ accessToken }) => this.setSession(accessToken)));
  }

  loginWithMicrosoft() {
    const apiOrigin = environment.api.replace(/\/api\/?$/, '');
    window.location.href = `${apiOrigin}/oauth/microsoft`;
  }

  logout() {
    this.tokenStore.clear();
    this.user.set(null);
    sessionStorage.removeItem(RETURN_URL_KEY);
    this.router.navigateByUrl('/login');
    return of(void 0);
  }

  consumeReturnUrl(): string | null {
    const url = sessionStorage.getItem(RETURN_URL_KEY);
    if (url) {
      sessionStorage.removeItem(RETURN_URL_KEY);
    }
    return url;
  }

  rememberReturnUrl(url: string): void {
    if (url && url !== '/login') {
      sessionStorage.setItem(RETURN_URL_KEY, url);
    }
  }

  consumeOauthError(): string | null {
    const error = sessionStorage.getItem(OAUTH_LOGIN_ERROR_STORAGE_KEY);
    if (error) {
      sessionStorage.removeItem(OAUTH_LOGIN_ERROR_STORAGE_KEY);
    }
    return error;
  }

  private handleOauthCallback(): void {
    const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
    if (!hash) {
      return;
    }

    const params = new URLSearchParams(hash);
    const accessToken = params.get('access_token');
    const error = params.get('error');

    history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);

    if (accessToken) {
      this.setSession(accessToken);
      const returnUrl = this.consumeReturnUrl();
      this.router.navigateByUrl(returnUrl || '/app');
      return;
    }

    if (error) {
      sessionStorage.setItem(OAUTH_LOGIN_ERROR_STORAGE_KEY, error);
      this.router.navigateByUrl('/login');
    }
  }

  private restoreSession(): void {
    const token = this.tokenStore.get();
    if (!token || isAccessTokenExpired(token)) {
      this.tokenStore.clear();
      this.user.set(null);
      return;
    }

    this.user.set(decodeAccessToken(token));
  }

  private setSession(accessToken: string): void {
    this.tokenStore.set(accessToken);
    this.user.set(decodeAccessToken(accessToken));
  }

  private readStoredUser(): AuthUserModel | null {
    const token = this.tokenStore.get();
    if (!token || isAccessTokenExpired(token)) {
      return null;
    }

    return decodeAccessToken(token);
  }
}
